import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  Chip,
  Drawer,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useMediaQuery,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import IntegrationHealthCard from '../components/domain/IntegrationHealthCard';
import EmptyState from '../components/domain/EmptyState';
import api from '../api/client';

const STATUS_CONFIG = {
  ok: { icon: <CheckCircleIcon fontSize="small" />, color: 'success', label: 'Działa' },
  warning: { icon: <WarningAmberIcon fontSize="small" />, color: 'warning', label: 'Wymaga uwagi' },
  stopped: { icon: <PauseCircleOutlineIcon fontSize="small" />, color: 'error', label: 'Zatrzymana' },
};

function Integrations() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newType, setNewType] = useState('');
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchIntegrations = () => {
    setLoading(true);
    api.get('/api/tenants/me/integrations')
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setIntegrations(list.map((i) => ({
          ...i,
          statusLabel: STATUS_CONFIG[i.status]?.label || i.status,
        })));
      })
      .catch(() => setIntegrations([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const overallOk = integrations.length > 0 && integrations.every((i) => i.status === 'ok');
  const lastSync = integrations[0]?.lastSync || '—';
  const healthSummary = integrations.map((i) => ({ name: i.name, status: i.statusLabel, ok: i.status === 'ok' }));

  const handleAdd = async () => {
    if (!newName || !newType) return;
    setSaving(true);
    try {
      await api.post('/api/tenants/me/integrations', {
        name: newName,
        type: newType,
        status: 'stopped',
        config: { url: newUrl },
      });
      setSnackbar({ open: true, message: 'Integracja dodana', severity: 'success' });
      setNewName('');
      setNewUrl('');
      setNewType('');
      setDrawerOpen(false);
      fetchIntegrations();
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd: ${err.body?.error || err.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async (id) => {
    try {
      await api.put(`/api/tenants/me/integrations/${id}`, { status: 'ok' });
      setSnackbar({ open: true, message: 'Synchronizacja zakończona', severity: 'success' });
      fetchIntegrations();
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd: ${err.body?.error || err.message}`, severity: 'error' });
    }
  };

  const handleToggle = async (id, current) => {
    const next = current === 'ok' ? 'stopped' : 'ok';
    try {
      await api.put(`/api/tenants/me/integrations/${id}`, { status: next });
      fetchIntegrations();
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd: ${err.body?.error || err.message}`, severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.del(`/api/tenants/me/integrations/${id}`);
      fetchIntegrations();
      setSnackbar({ open: true, message: 'Integracja usunięta', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd: ${err.body?.error || err.message}`, severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontFamily: theme.typography.h1.fontFamily, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Integracje
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Połączenia z zewnętrznymi systemami i trackerami
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="large"
          onClick={() => setDrawerOpen(true)}
          sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0, borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
        >
          Dodaj integrację
        </Button>
      </Box>

      {/* Overall health card */}
      <Box sx={{ mb: 4 }}>
        <IntegrationHealthCard
          overallOk={overallOk}
          lastSyncLabel={`Ostatnia synchronizacja: ${lastSync}`}
          integrations={healthSummary}
        />
      </Box>

      {/* Integration list */}
      <Typography variant="h6" sx={{ fontFamily: theme.typography.h1.fontFamily, mb: 2, fontSize: '1.1rem' }}>
        Wszystkie integracje
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {integrations.length === 0 && (
          <EmptyState title="Brak integracji" description="Dodaj pierwszą integrację, aby połączyć się ze zewnętrznymi systemami." />
        )}
        {integrations.map((integration) => {
          const statusConf = STATUS_CONFIG[integration.status] || STATUS_CONFIG.stopped;
          return (
            <Box
              key={integration.id}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                transition: 'box-shadow 160ms ease',
                '&:hover': { boxShadow: theme.tokens?.semantic?.shadow?.soft },
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                  <Typography variant="body1" sx={{ fontWeight: 650 }}>
                    {integration.name}
                  </Typography>
                  <Chip
                    icon={statusConf.icon}
                    label={integration.statusLabel}
                    color={statusConf.color}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {integration.type} · Ostatnia sync: {integration.lastSync || '—'}
                  {(integration.errors || 0) > 0 && ` · ${integration.errors} błędów`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" startIcon={<SyncIcon />} onClick={() => handleSync(integration.id)} sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}>
                  Synchronizuj
                </Button>
                <Button size="small" variant="outlined" startIcon={<SettingsOutlinedIcon />} onClick={() => handleToggle(integration.id, integration.status)} sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}>
                  {integration.status === 'ok' ? 'Wyłącz' : 'Włącz'}
                </Button>
                <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => handleDelete(integration.id)} sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}>
                  Usuń
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Setup Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            p: 3,
            borderRadius: isMobile ? 0 : `${theme.tokens?.primitive?.radius?.card ?? 20}px 0 0 ${theme.tokens?.primitive?.radius?.card ?? 20}px`,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontFamily: theme.typography.h1.fontFamily }}>
            Nowa integracja
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Typ integracji</InputLabel>
            <Select value={newType} onChange={(e) => setNewType(e.target.value)} label="Typ integracji">
              <MenuItem value="webhook">Webhook</MenuItem>
              <MenuItem value="scim">SCIM 2.0</MenuItem>
              <MenuItem value="rest">REST API</MenuItem>
              <MenuItem value="health">Health Tracker (Google Fit / Apple Health)</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Nazwa" size="small" fullWidth placeholder="np. Slack HR Channel" value={newName} onChange={(e) => setNewName(e.target.value)} />
          <TextField label="URL / Endpoint" size="small" fullWidth placeholder="https://..." value={newUrl} onChange={(e) => setNewUrl(e.target.value)} />
          <Button
            variant="contained"
            fullWidth
            disabled={!newName || !newType || saving}
            onClick={handleAdd}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{ mt: 2, borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
          >
            {saving ? 'Dodawanie…' : 'Dodaj integrację'}
          </Button>
        </Box>
      </Drawer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Integrations;
